import React, { useState, useEffect, useRef } from 'react';
import {
  IconSpeakerphone, IconCircleCheck, IconClock, IconCircleX,
  IconPlus, IconSend, IconFlame, IconEye, IconRefresh, IconHome,
  IconAlertTriangle, IconStar, IconClockOff, IconUser, IconCheck,
  IconDroplet, IconCrown, IconShoppingCartOff, IconUserPlus,
  IconActivity, IconUserOff, IconSparkles, IconHeart, IconTruckDelivery,
  IconBuilding, IconTool, IconCar, IconFileText,
  IconX, IconPhoto, IconCalendar, IconTag, IconUsers, IconSearch, IconUpload,
  IconExternalLink, IconPhone, IconMessage, IconTrash
} from '@tabler/icons-react';
import { META_STATUSES } from '../data/industries';

const ICON_MAP = {
  'ti-flame': IconFlame, 'ti-eye': IconEye, 'ti-refresh': IconRefresh,
  'ti-home': IconHome, 'ti-check': IconCheck, 'ti-clock-off': IconClockOff,
  'ti-user': IconUser, 'ti-star': IconStar, 'ti-alert-triangle': IconAlertTriangle,
  'ti-droplet': IconDroplet, 'ti-crown': IconCrown, 'ti-shopping-cart-off': IconShoppingCartOff,
  'ti-user-plus': IconUserPlus, 'ti-activity': IconActivity, 'ti-user-off': IconUserOff,
  'ti-sparkles': IconSparkles, 'ti-heart': IconHeart, 'ti-truck-delivery': IconTruckDelivery,
  'ti-building': IconBuilding, 'ti-tool': IconTool, 'ti-car': IconCar,
  'ti-file-text': IconFileText, 'ti-clock': IconClock,
};

const STATUS_COLORS = {
  sent: { bg: '#DCFCE7', color: '#16A34A' },
  scheduled: { bg: '#FEF3C7', color: '#D97706' },
  draft: { bg: '#F3F4F6', color: '#6B7280' },
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box'
};

// ── ToggleGroup ─────────────────────────────────────────────────────────────

function ToggleGroup({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: '#F3F4F6', borderRadius: 8, padding: 3 }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)} style={{
          flex: 1, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
          fontSize: 12, fontWeight: value === opt.value ? 600 : 400, fontFamily: 'inherit',
          background: value === opt.value ? '#fff' : 'transparent',
          color: value === opt.value ? '#5B2D8E' : '#6B7280',
          boxShadow: value === opt.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          transition: 'all 0.2s'
        }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── TagInput (from Contacts.jsx pattern) ────────────────────────────────────

function TagInput({ selectedTagIds, allTags, onChange, onCreateTag, placeholder, lang }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filtered = allTags.filter(tag =>
    !selectedTagIds.includes(tag.id) &&
    (tag.labelEn.toLowerCase().includes(query.toLowerCase()) ||
     (tag.labelAr || '').includes(query))
  );

  const exactMatch = allTags.some(
    t => t.labelEn.toLowerCase() === query.toLowerCase() ||
         (t.labelAr || '') === query
  );

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function removeTag(id) { onChange(selectedTagIds.filter(t => t !== id)); }
  function addTag(id) { onChange([...selectedTagIds, id]); setQuery(''); inputRef.current?.focus(); }
  function handleCreate() {
    if (!query.trim() || !onCreateTag) return;
    const newTag = onCreateTag(query.trim());
    onChange([...selectedTagIds, newTag.id]);
    setQuery(''); setOpen(false); inputRef.current?.focus();
  }

  const selectedTags = allTags.filter(t => selectedTagIds.includes(t.id));
  const isRtl = lang === 'ar';

  return (
    <div style={{ position: 'relative' }}>
      <div onClick={() => { setOpen(true); inputRef.current?.focus(); }} style={{
        minHeight: 40, padding: '6px 10px', borderRadius: 8,
        border: '1px solid #E5E7EB', background: '#fff',
        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', cursor: 'text'
      }}>
        {selectedTags.map(tag => (
          <span key={tag.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
            background: tag.color + '18', color: tag.color, border: `1px solid ${tag.color}40`
          }}>
            {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
            <button onClick={e => { e.stopPropagation(); removeTag(tag.id); }} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: tag.color, lineHeight: 1, display: 'flex', alignItems: 'center'
            }}><IconX size={12} /></button>
          </span>
        ))}
        <input ref={inputRef} value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          style={{
            border: 'none', outline: 'none', fontSize: 13, flex: 1,
            minWidth: 80, background: 'transparent', fontFamily: 'inherit',
            direction: isRtl ? 'rtl' : 'ltr'
          }}
        />
      </div>
      {open && (filtered.length > 0 || (query.trim() && !exactMatch)) && (
        <div ref={dropdownRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden'
        }}>
          {filtered.slice(0, 6).map(tag => (
            <div key={tag.id}
              onMouseDown={e => { e.preventDefault(); addTag(tag.id); }}
              style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: tag.color, flexShrink: 0 }} />
              {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
            </div>
          ))}
          {query.trim() && !exactMatch && onCreateTag && (
            <div onMouseDown={e => { e.preventDefault(); handleCreate(); }} style={{
              padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 13, fontWeight: 600, color: '#5B2D8E',
              borderTop: filtered.length > 0 ? '1px solid #F3F4F6' : 'none',
              direction: isRtl ? 'rtl' : 'ltr'
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#F7F2FC'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <IconPlus size={14} />
              {isRtl ? `إنشاء وسم "${query.trim()}"` : `Create tag "${query.trim()}"`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── TemplateSlideOver ───────────────────────────────────────────────────────

function TemplateSlideOver({ lang, t, onClose, onSubmit }) {
  const isRtl = lang === 'ar';
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [category, setCategory] = useState('marketing');
  const [templateLang, setTemplateLang] = useState('ar');
  const [type, setType] = useState('text');
  const [bodyAr, setBodyAr] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [variables, setVariables] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [buttons, setButtons] = useState([]);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  function addButton(btnType) {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { type: btnType, text: '', value: '' }]);
  }
  function updateButton(idx, field, val) {
    setButtons(buttons.map((b, i) => i === idx ? { ...b, [field]: val } : b));
  }
  function removeButton(idx) {
    setButtons(buttons.filter((_, i) => i !== idx));
  }

  function insertVariable(varName) {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const tag = `{{${varName}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const isAr = templateLang === 'ar';
    const current = isAr ? bodyAr : bodyEn;
    const updated = current.substring(0, start) + tag + current.substring(end);
    if (isAr) setBodyAr(updated); else setBodyEn(updated);
    if (!variables.includes(varName)) setVariables([...variables, varName]);
    setTimeout(() => { textarea.focus(); textarea.selectionStart = textarea.selectionEnd = start + tag.length; }, 0);
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  }

  function handleSubmit() {
    if (!nameAr && !nameEn) return;
    onSubmit({
      id: 'tmpl_' + Date.now(),
      nameAr: nameAr || nameEn, nameEn: nameEn || nameAr,
      category, lang: templateLang, type,
      bodyAr: bodyAr || bodyEn, bodyEn: bodyEn || bodyAr,
      variables, imageUrl: imagePreview,
      buttons: buttons.filter(b => b.text),
      metaStatus: 'pending'
    });
  }

  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200 }} />
      <div style={{
        position: 'fixed', top: 0, bottom: 0, [isRtl ? 'left' : 'right']: 0,
        width: 460, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column',
        animation: 'slideRight 0.25s ease', boxShadow: '-4px 0 24px rgba(0,0,0,0.08)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{t.createNewTemplate || t.createTemplate}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            borderRadius: 6, display: 'flex', color: '#6B7280'
          }}><IconX size={20} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Name AR */}
          <div>
            <label style={labelStyle}>{t.templateNameAr || 'Name (Arabic)'}</label>
            <input value={nameAr} onChange={e => setNameAr(e.target.value)}
              style={{ ...inputStyle, direction: 'rtl' }} placeholder="اسم القالب" />
          </div>
          {/* Name EN */}
          <div>
            <label style={labelStyle}>{t.templateNameEn || 'Name (English)'}</label>
            <input value={nameEn} onChange={e => setNameEn(e.target.value)}
              style={inputStyle} placeholder="Template name" />
          </div>
          {/* Category */}
          <div>
            <label style={labelStyle}>{t.templateCategory || 'Category'}</label>
            <ToggleGroup value={category} onChange={setCategory} options={[
              { value: 'marketing', label: t.categoryMarketing || 'Marketing' },
              { value: 'utility', label: t.categoryUtility || 'Utility' },
            ]} />
          </div>
          {/* Language */}
          <div>
            <label style={labelStyle}>{t.templateLang || 'Language'}</label>
            <ToggleGroup value={templateLang} onChange={setTemplateLang} options={[
              { value: 'ar', label: 'العربية' },
              { value: 'en', label: 'English' },
            ]} />
          </div>
          {/* Type */}
          <div>
            <label style={labelStyle}>{t.templateType || 'Template Type'}</label>
            <ToggleGroup value={type} onChange={setType} options={[
              { value: 'text', label: t.textOnly || 'Text Only' },
              { value: 'image_text', label: t.imageAndText || 'Image + Text' },
            ]} />
          </div>
          {/* Image upload */}
          {type === 'image_text' && (
            <div>
              <label style={labelStyle}>{t.uploadImage || 'Upload Image'}</label>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
              {imagePreview ? (
                <div style={{ position: 'relative' }}>
                  <img src={imagePreview} alt="" style={{ width: '100%', borderRadius: 8, maxHeight: 200, objectFit: 'cover' }} />
                  <button onClick={() => { setImagePreview(null); if (fileRef.current) fileRef.current.value = ''; }} style={{
                    position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none',
                    borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', color: '#fff'
                  }}><IconX size={14} /></button>
                </div>
              ) : (
                <div onClick={() => fileRef.current?.click()} style={{
                  border: '2px dashed #D1D5DB', borderRadius: 8, padding: '24px', textAlign: 'center',
                  cursor: 'pointer', color: '#9CA3AF', fontSize: 13
                }}>
                  <IconPhoto size={28} style={{ margin: '0 auto 8px', display: 'block' }} />
                  {t.uploadImage || 'Upload Image'}
                </div>
              )}
            </div>
          )}
          {/* Message body */}
          <div>
            <label style={labelStyle}>{t.messageBody || 'Message Body'}</label>
            <textarea ref={bodyRef}
              value={templateLang === 'ar' ? bodyAr : bodyEn}
              onChange={e => templateLang === 'ar' ? setBodyAr(e.target.value) : setBodyEn(e.target.value)}
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical', direction: templateLang === 'ar' ? 'rtl' : 'ltr' }}
              placeholder={templateLang === 'ar' ? 'اكتب رسالتك هنا... استخدم {{name}} للمتغيرات' : 'Type your message... Use {{name}} for variables'}
            />
          </div>
          {/* Live WhatsApp preview */}
          {(bodyAr || bodyEn) && (() => {
            const bodyText = templateLang === 'ar' ? (bodyAr || bodyEn) : (bodyEn || bodyAr);
            const hasImage = type === 'image_text' && imagePreview;
            const dir = templateLang === 'ar' ? 'rtl' : 'ltr';
            return (
              <div style={{
                borderRadius: 16, overflow: 'hidden',
                border: '1px solid #E5E7EB', background: '#ECE5DD',
                maxWidth: 320, alignSelf: 'center', width: '100%'
              }}>
                <div style={{
                  background: '#075E54', color: '#fff', padding: '8px 12px',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: '#25D366',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <IconUser size={16} color="#fff" />
                  </div>
                  <span>{t.livePreview || 'Live Preview'}</span>
                </div>
                <div style={{ padding: '14px 10px', minHeight: 60 }}>
                  <div style={{
                    background: '#fff', borderRadius: '0 8px 8px 8px', padding: hasImage ? 0 : '8px 10px',
                    maxWidth: '92%', boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                    overflow: 'hidden', direction: dir
                  }}>
                    {hasImage && (
                      <div style={{ width: '100%', height: 130, overflow: 'hidden' }}>
                        <img src={imagePreview} alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <div style={{ padding: hasImage ? '8px 10px' : 0 }}>
                      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: '#111', whiteSpace: 'pre-wrap' }}>
                        {bodyText}
                      </div>
                      <div style={{
                        fontSize: 10, color: '#999', textAlign: 'right',
                        marginTop: 4, display: 'flex', alignItems: 'center',
                        justifyContent: 'flex-end', gap: 3
                      }}>
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        <IconCheck size={12} color="#53bdeb" />
                      </div>
                    </div>
                    {/* Buttons in preview */}
                    {buttons.filter(b => b.text).length > 0 && (
                      <div style={{ borderTop: '1px solid #E5E7EB' }}>
                        {buttons.filter(b => b.text).map((btn, idx) => (
                          <div key={idx} style={{
                            padding: '7px 10px', textAlign: 'center', color: '#0891B2',
                            fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: 5,
                            borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none'
                          }}>
                            {btn.type === 'url' && <IconExternalLink size={13} />}
                            {btn.type === 'phone' && <IconPhone size={13} />}
                            {btn.type === 'quick_reply' && <IconMessage size={13} />}
                            {btn.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Variable dropdown */}
          <div>
            <label style={labelStyle}>{t.addVariable || 'Add Variable'}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                defaultValue=""
                onChange={e => { if (e.target.value) { insertVariable(e.target.value); e.target.value = ''; } }}
                style={{ ...inputStyle, flex: 1, fontSize: 13 }}
              >
                <option value="" disabled>{t.selectVariable || (lang === 'ar' ? 'اختر متغير...' : 'Select variable...')}</option>
                <option value="name">{t.variableName || (lang === 'ar' ? 'اسم جهة الاتصال' : 'Contact Name')}</option>
                <option value="phone">{t.variablePhone || (lang === 'ar' ? 'رقم الهاتف' : 'Phone Number')}</option>
              </select>
            </div>
            {variables.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {variables.map(v => (
                  <span key={v} style={{
                    padding: '3px 10px', borderRadius: 12, background: '#EDE9FE',
                    color: '#5B2D8E', fontSize: 11, fontWeight: 600
                  }}>
                    {'{{' + v + '}}'}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Buttons section */}
          <div>
            <label style={labelStyle}>{lang === 'ar' ? 'أزرار (اختياري)' : 'Buttons (optional)'}</label>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
              {lang === 'ar' ? 'أضف حتى 3 أزرار للرسالة' : 'Add up to 3 buttons to the message'}
            </div>
            {buttons.length < 3 && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {[
                  { type: 'quick_reply', label: lang === 'ar' ? 'رد سريع' : 'Quick Reply', color: '#5B2D8E' },
                  { type: 'url', label: lang === 'ar' ? 'زيارة رابط' : 'Visit Website', color: '#0891B2' },
                  { type: 'phone', label: lang === 'ar' ? 'اتصال' : 'Call Number', color: '#059669' },
                ].map(bt => (
                  <button key={bt.type} onClick={() => addButton(bt.type)} style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                    borderRadius: 16, border: 'none', background: bt.color, color: '#fff',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer'
                  }}>
                    <IconPlus size={12} /> {bt.label}
                  </button>
                ))}
              </div>
            )}
            {buttons.map((btn, idx) => {
              const typeLabel = btn.type === 'quick_reply' ? (lang === 'ar' ? 'رد سريع' : 'Quick Reply')
                : btn.type === 'url' ? (lang === 'ar' ? 'زيارة رابط' : 'Visit Website')
                : (lang === 'ar' ? 'اتصال' : 'Call Number');
              const typeColor = btn.type === 'quick_reply' ? '#5B2D8E' : btn.type === 'url' ? '#0891B2' : '#059669';
              return (
                <div key={idx} style={{
                  border: '1px solid #E5E7EB', borderRadius: 8, padding: 10,
                  marginBottom: 8, background: '#FAFAFA'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{
                      padding: '2px 10px', borderRadius: 10, background: typeColor,
                      color: '#fff', fontSize: 10, fontWeight: 700, textTransform: 'uppercase'
                    }}>{typeLabel}</span>
                    <button onClick={() => removeButton(idx)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 2
                    }}><IconTrash size={14} /></button>
                  </div>
                  <input value={btn.text} onChange={e => updateButton(idx, 'text', e.target.value)}
                    placeholder={lang === 'ar' ? 'نص الزر' : 'Button text'}
                    style={{ ...inputStyle, marginBottom: btn.type !== 'quick_reply' ? 6 : 0, fontSize: 12 }} />
                  {btn.type === 'url' && (
                    <input value={btn.value} onChange={e => updateButton(idx, 'value', e.target.value)}
                      placeholder="https://example.com"
                      style={{ ...inputStyle, fontSize: 12, direction: 'ltr' }} />
                  )}
                  {btn.type === 'phone' && (
                    <input value={btn.value} onChange={e => updateButton(idx, 'value', e.target.value)}
                      placeholder="+966 5XX XXX XXXX"
                      style={{ ...inputStyle, fontSize: 12, direction: 'ltr' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
          <button onClick={handleSubmit} disabled={!nameAr && !nameEn} style={{
            width: '100%', padding: '10px', borderRadius: 8, border: 'none',
            background: (nameAr || nameEn) ? '#5B2D8E' : '#D1D5DB',
            color: '#fff', cursor: (nameAr || nameEn) ? 'pointer' : 'default',
            fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
          }}>
            <IconSend size={16} /> {t.submitToMeta || 'Submit to Meta for Review'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── CampaignSlideOver ───────────────────────────────────────────────────────

function CampaignSlideOver({ lang, t, templates, segments, contacts, tags, onClose, onLaunch, onAddTag, onAddSegment }) {
  const isRtl = lang === 'ar';
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [audienceMode, setAudienceMode] = useState('segment');
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [saveAsSegment, setSaveAsSegment] = useState(false);
  const [newSegmentName, setNewSegmentName] = useState('');
  const [csvImportCount, setCsvImportCount] = useState(null);
  const csvFileRef = useRef(null);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagLabel, setNewTagLabel] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366F1');
  const [newTagContactIds, setNewTagContactIds] = useState([]);
  const [newTagContactSearch, setNewTagContactSearch] = useState('');
  const [showNewSegment, setShowNewSegment] = useState(false);
  const [newSegName, setNewSegName] = useState('');
  const [newSegTagIds, setNewSegTagIds] = useState([]);
  const [newSegContactIds, setNewSegContactIds] = useState([]);
  const [newSegContactSearch, setNewSegContactSearch] = useState('');
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [varMappings, setVarMappings] = useState({});
  const [activeVar, setActiveVar] = useState(null);
  const [customInput, setCustomInput] = useState('');
  const [campaignImage, setCampaignImage] = useState(null);
  const [templateDropOpen, setTemplateDropOpen] = useState(false);
  const campaignFileRef = useRef(null);

  const approvedTemplates = templates.filter(tmpl => tmpl.metaStatus === 'approved');
  const selectedTemplate = templates.find(tmpl => tmpl.id === selectedTemplateId);
  const selectedSegment = segments.find(s => s.id === selectedSegmentId);

  const matchingCount = audienceMode === 'segment'
    ? (selectedSegment?.size || 0)
    : audienceMode === 'tags'
      ? (selectedTagIds.length > 0 ? contacts.filter(c => selectedTagIds.every(tid => (c.tags || []).includes(tid))).length : 0)
      : selectedContactIds.length;

  function handleLaunch() {
    if (!selectedTemplateId) return;
    if (audienceMode === 'segment' && !selectedSegmentId) return;
    if (audienceMode === 'tags' && selectedTagIds.length === 0) return;
    if (audienceMode === 'customers' && selectedContactIds.length === 0) return;

    const seg = segments.find(s => s.id === selectedSegmentId);
    const tagLabels = selectedTagIds.map(tid => {
      const tag = tags.find(tg => tg.id === tid);
      return tag ? (lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn) : tid;
    }).join(', ');
    const customerNames = selectedContactIds.map(cid => {
      const c = contacts.find(ct => ct.id === cid);
      return c ? (lang === 'ar' ? c.nameAr : c.nameEn) : cid;
    }).join(', ');

    const audienceLabel = audienceMode === 'segment' ? (lang === 'ar' ? seg?.nameAr : seg?.nameEn) || ''
      : audienceMode === 'tags' ? tagLabels
      : (selectedContactIds.length + (lang === 'ar' ? ' عميل محدد' : ' selected customers'));

    onLaunch({
      id: 'camp_' + Date.now(),
      nameAr: nameAr || nameEn, nameEn: nameEn || nameAr,
      segmentAr: audienceLabel,
      segmentEn: audienceLabel,
      size: matchingCount,
      status: scheduleMode === 'now' ? 'sent' : 'scheduled',
      sent: scheduleMode === 'now' ? matchingCount : 0,
      opened: 0, responded: 0,
      metaStatus: 'approved',
      templateId: selectedTemplateId,
    });
  }

  const canLaunch = selectedTemplateId && (nameAr || nameEn) &&
    ((audienceMode === 'segment' && selectedSegmentId) || (audienceMode === 'tags' && selectedTagIds.length > 0) || (audienceMode === 'customers' && selectedContactIds.length > 0));

  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };

  // Build preview helpers
  const rawBody = selectedTemplate
    ? (lang === 'ar' ? (selectedTemplate.bodyAr || selectedTemplate.bodyEn || '') : (selectedTemplate.bodyEn || selectedTemplate.bodyAr || ''))
    : '';
  const previewParts = rawBody.split(/(\{\{\w+\}\})/g);
  const isImageTemplate = selectedTemplate && (selectedTemplate.type === 'image' || selectedTemplate.type === 'image_text');
  const campaignImageSrc = campaignImage || (selectedTemplate && selectedTemplate.imageUrl);
  const hasImage = isImageTemplate && campaignImageSrc;
  const templateDir = selectedTemplate ? (selectedTemplate.lang === 'ar' ? 'rtl' : 'ltr') : 'ltr';

  function resolveVar(varName) {
    const mapped = varMappings[varName];
    if (!mapped) return null;
    if (mapped === 'contact_name') return lang === 'ar' ? 'أحمد محمد' : 'Ahmed Mohammed';
    if (mapped === 'phone_number') return '+966 5X XXX XXXX';
    return mapped;
  }

  const presetOptions = [
    { value: 'contact_name', label: lang === 'ar' ? 'اسم جهة الاتصال' : 'Contact Name', icon: '👤' },
    { value: 'phone_number', label: lang === 'ar' ? 'رقم الهاتف' : 'Phone Number', icon: '📱' },
  ];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200 }} />
      <div style={{
        position: 'fixed', top: 0, bottom: 0, [isRtl ? 'left' : 'right']: 0,
        width: selectedTemplate ? 740 : 460, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column',
        animation: 'slideRight 0.25s ease', boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
        transition: 'width 0.25s ease'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{t.launchCampaign}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            borderRadius: 6, display: 'flex', color: '#6B7280'
          }}><IconX size={20} /></button>
        </div>

        {/* Two-column body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* LEFT — Form */}
          <div style={{ width: selectedTemplate ? 420 : '100%', overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, transition: 'width 0.25s ease' }}>
            {/* Campaign Name */}
            <div>
              <label style={labelStyle}>{t.campaignName || (lang === 'ar' ? 'اسم الحملة' : 'Campaign Name')}</label>
              <input value={nameAr} onChange={e => { setNameAr(e.target.value); setNameEn(e.target.value); }}
                style={inputStyle} placeholder={lang === 'ar' ? 'اسم الحملة' : 'Campaign name'} />
            </div>

            {/* Template picker */}
            <div>
              <label style={labelStyle}>{t.selectTemplate || 'Select Template'}</label>
              {templates.length === 0 ? (
                <div style={{ padding: 16, background: '#FEF3C7', borderRadius: 8, fontSize: 13, color: '#92400E', textAlign: 'center' }}>
                  {t.noApprovedTemplates || 'No approved templates yet'}
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Trigger */}
                  <div onClick={() => setTemplateDropOpen(!templateDropOpen)} style={{
                    ...inputStyle, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#fff', userSelect: 'none'
                  }}>
                    {selectedTemplate ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          background: '#DCFCE7', color: '#16A34A'
                        }}>
                          {lang === 'ar' ? 'معتمد' : 'Approved'}
                        </span>
                        {lang === 'ar' ? selectedTemplate.nameAr : selectedTemplate.nameEn}
                      </div>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>{t.selectTemplate || 'Select Template'}...</span>
                    )}
                    <span style={{ fontSize: 10, color: '#9CA3AF', transform: templateDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
                  </div>
                  {/* Dropdown list */}
                  {templateDropOpen && (<>
                    <div onClick={() => setTemplateDropOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 48 }} />
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                      background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 49,
                      maxHeight: 220, overflowY: 'auto'
                    }}>
                      {templates.map(tmpl => {
                        const name = lang === 'ar' ? tmpl.nameAr : tmpl.nameEn;
                        const isApproved = tmpl.metaStatus === 'approved';
                        const isPending = tmpl.metaStatus === 'pending';
                        const statusBg = isApproved ? '#DCFCE7' : isPending ? '#FEF3C7' : '#FEE2E2';
                        const statusColor = isApproved ? '#16A34A' : isPending ? '#D97706' : '#DC2626';
                        const statusText = isApproved
                          ? (lang === 'ar' ? 'معتمد' : 'Approved')
                          : isPending
                            ? (lang === 'ar' ? 'قيد المراجعة' : 'Pending')
                            : (lang === 'ar' ? 'مرفوض' : 'Rejected');
                        return (
                          <div key={tmpl.id}
                            onClick={() => {
                              if (!isApproved) return;
                              setSelectedTemplateId(tmpl.id); setVarMappings({}); setActiveVar(null); setCampaignImage(null); setTemplateDropOpen(false);
                            }}
                            style={{
                              padding: '8px 12px', cursor: isApproved ? 'pointer' : 'default',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                              opacity: isApproved ? 1 : 0.5,
                              background: selectedTemplateId === tmpl.id ? '#F3F0FF' : 'transparent',
                              borderBottom: '1px solid #F3F4F6'
                            }}
                            onMouseEnter={e => { if (isApproved) e.currentTarget.style.background = '#F9FAFB'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = selectedTemplateId === tmpl.id ? '#F3F0FF' : 'transparent'; }}
                          >
                            <span style={{ fontSize: 13, color: '#111' }}>{name}</span>
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                              background: statusBg, color: statusColor, whiteSpace: 'nowrap'
                            }}>
                              {statusText}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>)}
                </div>
              )}
            </div>

            {/* Audience */}
            <div>
              <label style={labelStyle}>{t.audience || 'Audience'}</label>
              <ToggleGroup value={audienceMode} onChange={v => { setAudienceMode(v); setSelectedSegmentId(''); setSelectedTagIds([]); setSelectedContactIds([]); setContactSearch(''); }} options={[
                { value: 'segment', label: t.bySegment || 'By Segment' },
                { value: 'tags', label: t.byTags || 'By Tags' },
                { value: 'customers', label: t.byCustomers || 'By Customers' },
              ]} />
              <div style={{ marginTop: 10 }}>
                {audienceMode === 'segment' ? (
                  <div>
                    <select value={selectedSegmentId} onChange={e => setSelectedSegmentId(e.target.value)}
                      style={{ ...inputStyle, fontSize: 14 }}>
                      <option value="">{t.selectSegment || 'Select Segment'}...</option>
                      {segments.map(seg => (
                        <option key={seg.id} value={seg.id}>
                          {lang === 'ar' ? seg.nameAr : seg.nameEn} ({seg.size})
                        </option>
                      ))}
                    </select>
                    {/* Inline create segment */}
                    {!showNewSegment ? (
                      <button onClick={() => setShowNewSegment(true)} style={{
                        marginTop: 8, background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, color: '#6366F1', display: 'flex', alignItems: 'center', gap: 4, padding: 0
                      }}>
                        <IconPlus size={14} /> {lang === 'ar' ? 'إنشاء شريحة جديدة' : 'Create new segment'}
                      </button>
                    ) : (
                      <div style={{
                        marginTop: 8, padding: 12, background: '#F9FAFB', borderRadius: 8,
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                          {lang === 'ar' ? 'شريحة جديدة' : 'New Segment'}
                        </div>
                        <input value={newSegName} onChange={e => setNewSegName(e.target.value)}
                          placeholder={lang === 'ar' ? 'اسم الشريحة' : 'Segment name'}
                          style={{ ...inputStyle, fontSize: 12, marginBottom: 8 }} />

                        {/* Tags filter */}
                        <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                          {lang === 'ar' ? 'فلتر حسب الوسوم' : 'Filter by tags'}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                          {tags.map(tag => {
                            const sel = newSegTagIds.includes(tag.id);
                            return (
                              <span key={tag.id} onClick={() => setNewSegTagIds(prev => sel ? prev.filter(id => id !== tag.id) : [...prev, tag.id])}
                                style={{
                                  fontSize: 11, padding: '3px 10px', borderRadius: 12, cursor: 'pointer',
                                  background: sel ? tag.color + '20' : '#F3F4F6',
                                  color: sel ? tag.color : '#6B7280', border: '1px solid ' + (sel ? tag.color : '#E5E7EB'),
                                  fontWeight: sel ? 600 : 400, transition: 'all 0.15s'
                                }}>
                                {lang === 'ar' ? (tag.labelAr || tag.labelEn) : tag.labelEn}
                              </span>
                            );
                          })}
                        </div>

                        {/* Add individual customers */}
                        <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                          {lang === 'ar' ? 'أو أضف عملاء يدوياً' : 'Or add customers manually'}
                        </div>
                        <div style={{ position: 'relative', marginBottom: 6 }}>
                          <IconSearch size={13} style={{ position: 'absolute', top: 8, [isRtl ? 'right' : 'left']: 8, color: '#9CA3AF' }} />
                          <input value={newSegContactSearch} onChange={e => setNewSegContactSearch(e.target.value)}
                            placeholder={lang === 'ar' ? 'ابحث...' : 'Search...'}
                            style={{ ...inputStyle, fontSize: 11, padding: '6px 8px 6px 28px' }} />
                        </div>
                        {newSegContactIds.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
                            {newSegContactIds.map(cid => {
                              const c = contacts.find(ct => ct.id === cid);
                              if (!c) return null;
                              return (
                                <span key={cid} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  background: '#EEF2FF', color: '#4338CA', fontSize: 10, fontWeight: 600,
                                  padding: '2px 6px', borderRadius: 10
                                }}>
                                  {lang === 'ar' ? c.nameAr : c.nameEn}
                                  <span onClick={() => setNewSegContactIds(prev => prev.filter(id => id !== cid))}
                                    style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.6 }}>×</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ maxHeight: 100, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', marginBottom: 8 }}>
                          {contacts.filter(c => {
                            const q = newSegContactSearch.toLowerCase();
                            if (!q) return true;
                            return (c.nameAr || '').toLowerCase().includes(q) || (c.nameEn || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
                          }).map(c => {
                            const sel = newSegContactIds.includes(c.id);
                            return (
                              <div key={c.id} onClick={() => setNewSegContactIds(prev => sel ? prev.filter(id => id !== c.id) : [...prev, c.id])}
                                style={{
                                  padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                  background: sel ? '#EEF2FF' : 'transparent', borderBottom: '1px solid #F3F4F6', fontSize: 11
                                }}>
                                <div style={{
                                  width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                                  border: '1.5px solid ' + (sel ? '#6366F1' : '#D1D5DB'),
                                  background: sel ? '#6366F1' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                  {sel && <IconCheck size={9} color="#fff" />}
                                </div>
                                <span style={{ fontWeight: 600, color: '#1F2937' }}>{lang === 'ar' ? c.nameAr : c.nameEn}</span>
                                <span style={{ color: '#9CA3AF', marginLeft: 'auto' }}>{c.phone}</span>
                              </div>
                            );
                          })}
                          {newSegContactSearch && contacts.filter(c => {
                            const q = newSegContactSearch.toLowerCase();
                            return (c.nameAr || '').toLowerCase().includes(q) || (c.nameEn || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
                          }).length === 0 && (
                            <div style={{ padding: '8px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
                              {lang === 'ar' ? 'لا نتائج' : 'No results'}
                            </div>
                          )}
                        </div>

                        {/* Size preview */}
                        {(() => {
                          const tagMatched = newSegTagIds.length > 0
                            ? contacts.filter(c => newSegTagIds.every(tid => (c.tags || []).includes(tid))).map(c => c.id)
                            : [];
                          const totalIds = [...new Set([...tagMatched, ...newSegContactIds])];
                          return totalIds.length > 0 ? (
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#22C55E', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <IconUsers size={13} /> {totalIds.length} {lang === 'ar' ? 'عميل' : 'customers'}
                            </div>
                          ) : null;
                        })()}

                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => {
                            if (!newSegName) return;
                            const tagMatched = newSegTagIds.length > 0
                              ? contacts.filter(c => newSegTagIds.every(tid => (c.tags || []).includes(tid))).map(c => c.id)
                              : [];
                            const totalIds = [...new Set([...tagMatched, ...newSegContactIds])];
                            const newSeg = {
                              id: 'seg_' + Date.now(),
                              nameAr: newSegName, nameEn: newSegName,
                              size: totalIds.length, color: '#6366F1', icon: 'ti-check', tags: newSegTagIds
                            };
                            onAddSegment(newSeg);
                            setSelectedSegmentId(newSeg.id);
                            setShowNewSegment(false); setNewSegName(''); setNewSegTagIds([]); setNewSegContactIds([]); setNewSegContactSearch('');
                          }} style={{
                            flex: 1, padding: '6px 0', borderRadius: 6, border: 'none',
                            background: '#6366F1', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            {lang === 'ar' ? 'إضافة' : 'Add'}
                          </button>
                          <button onClick={() => { setShowNewSegment(false); setNewSegName(''); setNewSegTagIds([]); setNewSegContactIds([]); setNewSegContactSearch(''); }}
                            style={{
                              padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
                              background: '#fff', fontSize: 12, color: '#6B7280', cursor: 'pointer'
                            }}>
                            {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : audienceMode === 'tags' ? (
                  <div>
                    <TagInput
                      selectedTagIds={selectedTagIds}
                      allTags={tags}
                      onChange={setSelectedTagIds}
                      placeholder={t.selectTags || 'Select Tags'}
                      lang={lang}
                    />
                    {/* Inline create tag */}
                    {!showNewTag ? (
                      <button onClick={() => setShowNewTag(true)} style={{
                        marginTop: 8, background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, color: '#6366F1', display: 'flex', alignItems: 'center', gap: 4, padding: 0
                      }}>
                        <IconPlus size={14} /> {lang === 'ar' ? 'إنشاء وسم جديد' : 'Create new tag'}
                      </button>
                    ) : (
                      <div style={{
                        marginTop: 8, padding: 12, background: '#F9FAFB', borderRadius: 8,
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                          {lang === 'ar' ? 'وسم جديد' : 'New Tag'}
                        </div>
                        <input value={newTagLabel} onChange={e => setNewTagLabel(e.target.value)}
                          placeholder={lang === 'ar' ? 'اسم الوسم' : 'Tag name'}
                          style={{ ...inputStyle, fontSize: 12, marginBottom: 6 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                          <span style={{ fontSize: 11, color: '#6B7280' }}>{lang === 'ar' ? 'اللون' : 'Color'}</span>
                          {['#EF4444', '#F59E0B', '#22C55E', '#3B82F6', '#6366F1', '#EC4899', '#1B6B93', '#9CA3AF'].map(c => (
                            <span key={c} onClick={() => setNewTagColor(c)} style={{
                              width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer',
                              border: newTagColor === c ? '2px solid #1F2937' : '2px solid transparent',
                              transition: 'border 0.15s'
                            }} />
                          ))}
                        </div>

                        {/* Assign customers to tag */}
                        <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                          {lang === 'ar' ? 'أضف عملاء لهذا الوسم' : 'Add customers to this tag'}
                        </div>
                        <div style={{ position: 'relative', marginBottom: 6 }}>
                          <IconSearch size={13} style={{ position: 'absolute', top: 8, [isRtl ? 'right' : 'left']: 8, color: '#9CA3AF' }} />
                          <input value={newTagContactSearch} onChange={e => setNewTagContactSearch(e.target.value)}
                            placeholder={lang === 'ar' ? 'ابحث...' : 'Search...'}
                            style={{ ...inputStyle, fontSize: 11, padding: '6px 8px 6px 28px' }} />
                        </div>
                        {newTagContactIds.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
                            {newTagContactIds.map(cid => {
                              const c = contacts.find(ct => ct.id === cid);
                              if (!c) return null;
                              return (
                                <span key={cid} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  background: newTagColor + '20', color: newTagColor, fontSize: 10, fontWeight: 600,
                                  padding: '2px 6px', borderRadius: 10
                                }}>
                                  {lang === 'ar' ? c.nameAr : c.nameEn}
                                  <span onClick={() => setNewTagContactIds(prev => prev.filter(id => id !== cid))}
                                    style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.6 }}>×</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ maxHeight: 100, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', marginBottom: 8 }}>
                          {contacts.filter(c => {
                            const q = newTagContactSearch.toLowerCase();
                            if (!q) return true;
                            return (c.nameAr || '').toLowerCase().includes(q) || (c.nameEn || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
                          }).map(c => {
                            const sel = newTagContactIds.includes(c.id);
                            return (
                              <div key={c.id} onClick={() => setNewTagContactIds(prev => sel ? prev.filter(id => id !== c.id) : [...prev, c.id])}
                                style={{
                                  padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                  background: sel ? newTagColor + '10' : 'transparent', borderBottom: '1px solid #F3F4F6', fontSize: 11
                                }}>
                                <div style={{
                                  width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                                  border: '1.5px solid ' + (sel ? newTagColor : '#D1D5DB'),
                                  background: sel ? newTagColor : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                  {sel && <IconCheck size={9} color="#fff" />}
                                </div>
                                <span style={{ fontWeight: 600, color: '#1F2937' }}>{lang === 'ar' ? c.nameAr : c.nameEn}</span>
                                <span style={{ color: '#9CA3AF', marginLeft: 'auto' }}>{c.phone}</span>
                              </div>
                            );
                          })}
                          {newTagContactSearch && contacts.filter(c => {
                            const q = newTagContactSearch.toLowerCase();
                            return (c.nameAr || '').toLowerCase().includes(q) || (c.nameEn || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
                          }).length === 0 && (
                            <div style={{ padding: '8px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
                              {lang === 'ar' ? 'لا نتائج' : 'No results'}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => {
                            if (!newTagLabel) return;
                            const newTag = {
                              id: newTagLabel.toLowerCase().replace(/\s+/g, '_'),
                              labelEn: newTagLabel, labelAr: newTagLabel,
                              color: newTagColor
                            };
                            onAddTag(newTag);
                            setSelectedTagIds(prev => [...prev, newTag.id]);
                            setShowNewTag(false); setNewTagLabel(''); setNewTagColor('#6366F1'); setNewTagContactIds([]); setNewTagContactSearch('');
                          }} style={{
                            flex: 1, padding: '6px 0', borderRadius: 6, border: 'none',
                            background: '#6366F1', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                          }}>
                            {lang === 'ar' ? 'إضافة' : 'Add'}
                          </button>
                          <button onClick={() => { setShowNewTag(false); setNewTagLabel(''); setNewTagColor('#6366F1'); setNewTagContactIds([]); setNewTagContactSearch(''); }}
                            style={{
                              padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
                              background: '#fff', fontSize: 12, color: '#6B7280', cursor: 'pointer'
                            }}>
                            {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Customer search & multi-select */
                  <div>
                    {/* Search + Import row */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <IconSearch size={15} style={{ position: 'absolute', top: 10, [isRtl ? 'right' : 'left']: 10, color: '#9CA3AF' }} />
                        <input
                          value={contactSearch} onChange={e => setContactSearch(e.target.value)}
                          placeholder={lang === 'ar' ? 'ابحث عن عميل...' : 'Search customers...'}
                          style={{ ...inputStyle, fontSize: 13, [isRtl ? 'paddingRight' : 'paddingLeft']: 32 }}
                        />
                      </div>
                      <input ref={csvFileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const text = ev.target.result;
                          const lines = text.split(/\r?\n/).filter(l => l.trim());
                          const phones = [];
                          const names = [];
                          lines.forEach((line, i) => {
                            const cols = line.split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
                            cols.forEach(col => {
                              if (/^\+?\d[\d\s\-]{6,}$/.test(col.replace(/\s/g, ''))) phones.push(col.replace(/[\s\-]/g, ''));
                              else if (col.length > 1 && i > 0) names.push(col.toLowerCase());
                            });
                          });
                          const matched = contacts.filter(c => {
                            const ph = (c.phone || '').replace(/[\s\-]/g, '');
                            if (phones.some(p => ph.includes(p) || p.includes(ph.slice(-9)))) return true;
                            if (names.some(n => (c.nameEn || '').toLowerCase().includes(n) || (c.nameAr || '').includes(n))) return true;
                            return false;
                          });
                          const newIds = [...new Set([...selectedContactIds, ...matched.map(c => c.id)])];
                          setSelectedContactIds(newIds);
                          setCsvImportCount(matched.length);
                          setTimeout(() => setCsvImportCount(null), 3000);
                        };
                        reader.readAsText(file);
                        e.target.value = '';
                      }} />
                      <button onClick={() => csvFileRef.current?.click()} style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px',
                        background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 8,
                        cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#6B7280',
                        whiteSpace: 'nowrap', transition: 'all 0.15s'
                      }} title={lang === 'ar' ? 'استيراد CSV' : 'Import CSV'}>
                        <IconUpload size={14} />
                        CSV
                      </button>
                    </div>
                    {/* CSV import feedback */}
                    {csvImportCount !== null && (
                      <div style={{
                        marginTop: 6, fontSize: 11, fontWeight: 600, padding: '4px 8px',
                        background: '#ECFDF5', color: '#059669', borderRadius: 6,
                        display: 'inline-flex', alignItems: 'center', gap: 4
                      }}>
                        <IconCheck size={13} />
                        {csvImportCount} {lang === 'ar' ? 'عميل تم مطابقتهم' : 'customers matched'}
                      </div>
                    )}
                    {/* Selected chips */}
                    {selectedContactIds.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                        {selectedContactIds.map(cid => {
                          const c = contacts.find(ct => ct.id === cid);
                          if (!c) return null;
                          return (
                            <span key={cid} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: '#EEF2FF', color: '#4338CA', fontSize: 11, fontWeight: 600,
                              padding: '3px 8px', borderRadius: 12
                            }}>
                              {lang === 'ar' ? c.nameAr : c.nameEn}
                              <span onClick={() => setSelectedContactIds(prev => prev.filter(id => id !== cid))}
                                style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1, marginLeft: 2, opacity: 0.6 }}>×</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Contact list */}
                    <div style={{
                      marginTop: 8, maxHeight: 160, overflowY: 'auto',
                      border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff'
                    }}>
                      {contacts.filter(c => {
                        const q = contactSearch.toLowerCase();
                        if (!q) return true;
                        return (c.nameAr || '').toLowerCase().includes(q) || (c.nameEn || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
                      }).map(c => {
                        const selected = selectedContactIds.includes(c.id);
                        return (
                          <div key={c.id} onClick={() => {
                            setSelectedContactIds(prev => selected ? prev.filter(id => id !== c.id) : [...prev, c.id]);
                          }} style={{
                            padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                            background: selected ? '#EEF2FF' : 'transparent',
                            borderBottom: '1px solid #F3F4F6',
                            transition: 'background 0.15s'
                          }}>
                            <div style={{
                              width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (selected ? '#6366F1' : '#D1D5DB'),
                              background: selected ? '#6366F1' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s', flexShrink: 0
                            }}>
                              {selected && <IconCheck size={12} color="#fff" />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
                                {lang === 'ar' ? c.nameAr : c.nameEn}
                              </div>
                              <div style={{ fontSize: 11, color: '#9CA3AF' }}>{c.phone}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Save as segment option */}
                    {selectedContactIds.length >= 2 && (
                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#6366F1', fontWeight: 600 }}>
                          <input type="checkbox" checked={saveAsSegment} onChange={e => setSaveAsSegment(e.target.checked)}
                            style={{ accentColor: '#6366F1' }} />
                          {lang === 'ar' ? 'حفظ كشريحة جديدة' : 'Save as new segment'}
                        </label>
                        {saveAsSegment && (
                          <input value={newSegmentName} onChange={e => setNewSegmentName(e.target.value)}
                            placeholder={lang === 'ar' ? 'اسم الشريحة...' : 'Segment name...'}
                            style={{ ...inputStyle, marginTop: 6, fontSize: 12 }} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {matchingCount > 0 && (
                <div style={{
                  marginTop: 8, display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 13, fontWeight: 600, color: '#22C55E'
                }}>
                  <IconUsers size={16} />
                  {matchingCount} {t.matchingContacts || 'matching contacts'}
                </div>
              )}
            </div>

            {/* Schedule */}
            <div>
              <label style={labelStyle}>{t.schedule || 'Schedule'}</label>
              <ToggleGroup value={scheduleMode} onChange={setScheduleMode} options={[
                { value: 'now', label: t.sendNow || 'Send Now' },
                { value: 'schedule', label: t.scheduleFor || 'Schedule For' },
              ]} />
              {scheduleMode === 'schedule' && (
                <input type="datetime-local" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                  style={{ ...inputStyle, marginTop: 10, fontSize: 14 }} />
              )}
            </div>
          </div>

          {/* RIGHT — WhatsApp Preview */}
          {selectedTemplate && (
            <div style={{
              width: 320, borderLeft: '1px solid #E5E7EB', background: '#F9FAFB',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '24px 16px', overflowY: 'auto'
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, textAlign: 'center' }}>
                {t.templatePreview || 'Template Preview'}
              </div>
              {/* Phone frame */}
              <div style={{
                borderRadius: 20, overflow: 'visible', border: '2px solid #D1D5DB', background: '#ECE5DD',
                width: '100%', maxWidth: 280, boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
              }}>
                {/* WhatsApp top bar */}
                <div style={{
                  background: '#075E54', color: '#fff', padding: '10px 12px',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600,
                  borderRadius: '18px 18px 0 0'
                }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', background: '#25D366',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <IconUser size={16} color="#fff" />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{lang === 'ar' ? 'العميل' : 'Customer'}</div>
                    <div style={{ fontSize: 10, opacity: 0.7 }}>online</div>
                  </div>
                </div>
                {/* Chat area */}
                <input ref={campaignFileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onloadend = () => setCampaignImage(r.result); r.readAsDataURL(f); } }} />
                <div style={{ padding: '16px 10px', minHeight: 120 }}>
                  <div style={{
                    background: '#fff', borderRadius: '0 10px 10px 10px', padding: (hasImage || (isImageTemplate && !campaignImageSrc)) ? 0 : '10px 12px',
                    maxWidth: '95%', boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    overflow: 'visible', direction: templateDir, position: 'relative'
                  }}>
                    {/* Image: show uploaded/existing, or upload placeholder */}
                    {isImageTemplate && (
                      campaignImageSrc ? (
                        <div style={{
                          width: '100%', height: 120, background: '#c8d6e5',
                          borderRadius: '0 10px 0 0', overflow: 'hidden', position: 'relative', cursor: 'pointer'
                        }} onClick={() => campaignFileRef.current?.click()}>
                          <img src={campaignImageSrc} alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                          <div style={{
                            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: 0, transition: 'opacity 0.15s'
                          }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                            <IconPhoto size={20} color="#fff" />
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => campaignFileRef.current?.click()}
                          style={{
                            width: '100%', height: 100, background: '#F3F4F6',
                            borderRadius: '0 10px 0 0', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 4,
                            cursor: 'pointer', border: '2px dashed #D1D5DB', borderBottom: 'none'
                          }}
                        >
                          <IconPhoto size={22} color="#9CA3AF" />
                          <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>
                            {lang === 'ar' ? 'ارفع صورة' : 'Upload Image'}
                          </span>
                        </div>
                      )
                    )}
                    <div style={{ padding: (isImageTemplate) ? '10px 12px' : 0 }}>
                      <div style={{ fontSize: 12.5, lineHeight: 2, color: '#111', whiteSpace: 'pre-wrap' }}>
                        {previewParts.map((part, i) => {
                          const varMatch = part.match(/^\{\{(\w+)\}\}$/);
                          if (!varMatch) return <span key={i}>{part}</span>;
                          const varName = varMatch[1];
                          const resolved = resolveVar(varName);
                          const isActive = activeVar === varName;
                          return (
                            <span key={i} style={{ position: 'relative', display: 'inline-block' }}>
                              <span
                                onClick={() => { setActiveVar(isActive ? null : varName); setCustomInput(''); }}
                                style={{
                                  background: resolved ? '#D1FAE5' : '#EDE9FE',
                                  color: resolved ? '#065F46' : '#5B2D8E',
                                  padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                                  fontSize: 11.5, fontWeight: 600,
                                  border: isActive ? '2px solid #5B2D8E' : '1px dashed ' + (resolved ? '#34D399' : '#A78BFA'),
                                  transition: 'all 0.15s ease', whiteSpace: 'nowrap'
                                }}
                              >
                                {resolved || `{{${varName}}}`}
                              </span>
                              {/* Popover — opens below, compact */}
                              {isActive && (<>
                                <div onClick={() => setActiveVar(null)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                                <div onClick={e => e.stopPropagation()} style={{
                                  position: 'absolute', top: '100%', left: 0,
                                  marginTop: 6, background: '#fff', borderRadius: 8,
                                  boxShadow: '0 3px 12px rgba(0,0,0,0.15)', border: '1px solid #E5E7EB',
                                  padding: 6, zIndex: 50, width: 150
                                }}>
                                  {presetOptions.map(opt => (
                                    <div key={opt.value}
                                      onClick={() => { setVarMappings(prev => ({ ...prev, [varName]: opt.value })); setActiveVar(null); }}
                                      style={{
                                        padding: '5px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                                        display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500,
                                        background: varMappings[varName] === opt.value ? '#F3F0FF' : 'transparent',
                                        color: '#374151'
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                                      onMouseLeave={e => e.currentTarget.style.background = varMappings[varName] === opt.value ? '#F3F0FF' : 'transparent'}
                                    >
                                      <span style={{ fontSize: 13 }}>{opt.icon}</span>
                                      {opt.label}
                                    </div>
                                  ))}
                                  <div style={{ height: 1, background: '#E5E7EB', margin: '4px 0' }} />
                                  <div style={{ display: 'flex', gap: 3 }}>
                                    <input
                                      value={customInput}
                                      onChange={e => setCustomInput(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' && customInput.trim()) {
                                          setVarMappings(prev => ({ ...prev, [varName]: customInput.trim() }));
                                          setActiveVar(null); setCustomInput('');
                                        }
                                      }}
                                      placeholder={lang === 'ar' ? 'اكتب...' : 'Type...'}
                                      autoFocus
                                      style={{
                                        flex: 1, padding: '3px 6px', borderRadius: 5, fontSize: 11,
                                        border: '1px solid #D1D5DB', outline: 'none', fontFamily: 'inherit',
                                        direction: lang === 'ar' ? 'rtl' : 'ltr'
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        if (customInput.trim()) {
                                          setVarMappings(prev => ({ ...prev, [varName]: customInput.trim() }));
                                          setActiveVar(null); setCustomInput('');
                                        }
                                      }}
                                      style={{
                                        padding: '3px 6px', borderRadius: 5, border: 'none',
                                        background: customInput.trim() ? '#5B2D8E' : '#E5E7EB',
                                        color: '#fff', cursor: customInput.trim() ? 'pointer' : 'default',
                                        fontSize: 11, fontFamily: 'inherit'
                                      }}
                                    >
                                      <IconCheck size={12} />
                                    </button>
                                  </div>
                                </div>
                              </>)}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{
                        fontSize: 10, color: '#999', textAlign: 'right',
                        marginTop: 4, display: 'flex', alignItems: 'center',
                        justifyContent: 'flex-end', gap: 3
                      }}>
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        <IconCheck size={12} color="#53bdeb" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* WhatsApp input bar */}
                <div style={{
                  padding: '8px 10px', background: '#F0F0F0', borderRadius: '0 0 18px 18px',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <div style={{
                    flex: 1, background: '#fff', borderRadius: 20, padding: '6px 12px',
                    fontSize: 11, color: '#999'
                  }}>
                    {lang === 'ar' ? 'اكتب رسالة...' : 'Type a message...'}
                  </div>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconSend size={14} color="#fff" />
                  </div>
                </div>
              </div>

              {/* Hint text */}
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
                {lang === 'ar' ? 'اضغط على المتغيرات لتعديلها' : 'Click variables to edit them'}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
          <button onClick={handleLaunch} disabled={!canLaunch} style={{
            width: '100%', padding: '10px', borderRadius: 8, border: 'none',
            background: canLaunch ? '#B5651D' : '#D1D5DB',
            color: '#fff', cursor: canLaunch ? 'pointer' : 'default',
            fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
          }}>
            <IconSend size={16} /> {t.launchCampaignBtn || t.launchCampaign}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Marketing Component ────────────────────────────────────────────────

export default function Marketing({ lang, t, industry, demoPhase, notify }) {
  const [tab, setTab] = useState('campaigns');
  const [showTemplateDrawer, setShowTemplateDrawer] = useState(false);
  const [showCampaignDrawer, setShowCampaignDrawer] = useState(false);
  const [localTemplates, setLocalTemplates] = useState(industry.templates || []);
  const [localCampaigns, setLocalCampaigns] = useState(industry.campaigns || []);

  useEffect(() => {
    setLocalTemplates(industry.templates || []);
    setLocalCampaigns(industry.campaigns || []);
  }, [industry]);

  const [localSegments, setLocalSegments] = useState(industry.segments || []);
  const [localTags, setLocalTags] = useState(industry.tags || []);
  const contacts = industry.contacts || [];

  useEffect(() => {
    setLocalSegments(industry.segments || []);
    setLocalTags(industry.tags || []);
  }, [industry]);

  function handleNewTemplate(tmpl) {
    setLocalTemplates(prev => [...prev, tmpl]);
    notify(t.templateSubmitted || 'Template submitted for review');
    setShowTemplateDrawer(false);
  }

  function handleLaunchCampaign(campaign) {
    setLocalCampaigns(prev => [...prev, campaign]);
    notify(campaign.status === 'sent'
      ? (t.campaignLaunched || 'Campaign launched successfully')
      : (t.campaignScheduled || 'Campaign scheduled successfully'));
    setShowCampaignDrawer(false);
  }

  const tabs = [
    { id: 'campaigns', label: t.campaigns },
    { id: 'templates', label: t.templates },
    { id: 'segments', label: t.segments },
  ];

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#F3F4F6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: tab === tb.id ? 600 : 400, fontFamily: 'inherit',
            background: tab === tb.id ? '#fff' : 'transparent',
            color: tab === tb.id ? '#5B2D8E' : '#6B7280',
            boxShadow: tab === tb.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s'
          }}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* CAMPAIGNS TAB */}
      {tab === 'campaigns' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => setShowCampaignDrawer(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
              background: '#5B2D8E', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: 600, fontFamily: 'inherit'
            }}>
              <IconPlus size={16} /> {t.launchCampaign}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {localCampaigns.map((c, i) => {
              const statusStyle = STATUS_COLORS[c.status] || STATUS_COLORS.draft;
              const meta = META_STATUSES[c.metaStatus];
              const MetaIcon = c.metaStatus === 'approved' ? IconCircleCheck : c.metaStatus === 'pending' ? IconClock : IconCircleX;
              return (
                <div key={c.id || i} style={{
                  background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
                  animation: `slideUp 0.3s ease ${i * 0.08}s both`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                        {lang === 'ar' ? c.nameAr : c.nameEn}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>
                        {lang === 'ar' ? c.segmentAr : c.segmentEn} — {c.size} {t.contacts}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      background: statusStyle.bg, color: statusStyle.color
                    }}>
                      {t[c.status]}
                    </span>
                  </div>

                  {/* Meta status */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                    borderRadius: 6, background: meta?.color + '15', marginBottom: 12,
                    fontSize: 12, fontWeight: 600, color: meta?.color
                  }}>
                    <MetaIcon size={14} stroke={2} />
                    {lang === 'ar' ? meta?.labelAr : meta?.labelEn}
                  </div>

                  {/* Metrics if sent */}
                  {c.status === 'sent' && (
                    <div style={{ display: 'flex', gap: 16 }}>
                      <div className="font-mono" style={{ fontSize: 12, color: '#6B7280' }}>
                        <span style={{ fontWeight: 600, color: '#1A1A2E' }}>{c.sent}</span> {t.sent}
                      </div>
                      <div className="font-mono" style={{ fontSize: 12, color: '#6B7280' }}>
                        <span style={{ fontWeight: 600, color: '#22C55E' }}>{c.opened}</span> {t.opened}
                      </div>
                      <div className="font-mono" style={{ fontSize: 12, color: '#6B7280' }}>
                        <span style={{ fontWeight: 600, color: '#5B2D8E' }}>{c.responded}</span> {t.responded}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TEMPLATES TAB */}
      {tab === 'templates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => setShowTemplateDrawer(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
              background: '#5B2D8E', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: 600, fontFamily: 'inherit'
            }}>
              <IconPlus size={16} /> {t.createTemplate}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {localTemplates.map((tmpl, i) => {
              const meta = META_STATUSES[tmpl.metaStatus];
              const MetaIcon = tmpl.metaStatus === 'approved' ? IconCircleCheck : tmpl.metaStatus === 'pending' ? IconClock : IconCircleX;
              const bodyPreview = lang === 'ar' ? (tmpl.bodyAr || tmpl.bodyEn || '') : (tmpl.bodyEn || tmpl.bodyAr || '');
              return (
                <div key={tmpl.id || i} style={{
                  background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #E5E7EB',
                  animation: `slideUp 0.3s ease ${i * 0.08}s both`
                }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
                    {lang === 'ar' ? tmpl.nameAr : tmpl.nameEn}
                  </div>
                  {/* Body preview */}
                  {bodyPreview && (
                    <div style={{
                      fontSize: 12, color: '#9CA3AF', marginBottom: 10, lineHeight: 1.5,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      direction: tmpl.lang === 'ar' ? 'rtl' : 'ltr'
                    }}>
                      {bodyPreview.length > 80 ? bodyPreview.slice(0, 80) + '...' : bodyPreview}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 4, background: '#F3F4F6',
                        fontSize: 11, color: '#6B7280', fontWeight: 500
                      }}>
                        {tmpl.category === 'marketing' ? (t.categoryMarketing || 'Marketing') : (t.categoryUtility || 'Utility')}
                      </span>
                      {tmpl.type && (
                        <span style={{
                          padding: '3px 8px', borderRadius: 4, background: tmpl.type === 'image_text' ? '#DBEAFE' : '#F3F4F6',
                          fontSize: 11, color: tmpl.type === 'image_text' ? '#2563EB' : '#6B7280', fontWeight: 500
                        }}>
                          {tmpl.type === 'image_text' ? (t.imageAndText || 'Image + Text') : (t.textOnly || 'Text')}
                        </span>
                      )}
                      <span style={{
                        padding: '3px 8px', borderRadius: 4, background: '#F3F4F6',
                        fontSize: 11, color: '#6B7280', fontWeight: 500
                      }}>
                        {tmpl.lang === 'ar' ? 'AR' : 'EN'}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: 12, fontWeight: 600, color: meta?.color
                    }}>
                      <MetaIcon size={14} stroke={2} />
                      {lang === 'ar' ? meta?.labelAr : meta?.labelEn}
                    </div>
                  </div>
                  {tmpl.metaStatus === 'pending' && (
                    <button style={{
                      marginTop: 10, width: '100%', padding: '6px', borderRadius: 6,
                      border: '1px solid #F59E0B', background: '#FEF3C7', color: '#D97706',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
                    }}>
                      {t.submitReview}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SEGMENTS TAB */}
      {tab === 'segments' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t.aiSegments}</h3>
            <p style={{ fontSize: 13, color: '#6B7280' }}>{t.builtByLatifa}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {localSegments.map((seg, i) => {
              const SegIcon = ICON_MAP[seg.icon] || IconUser;
              return (
                <div key={i} style={{
                  background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
                  animation: `slideUp 0.3s ease ${i * 0.08}s both`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: seg.color + '15',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12
                  }}>
                    <SegIcon size={22} color={seg.color} stroke={1.8} />
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                    {lang === 'ar' ? seg.nameAr : seg.nameEn}
                  </div>
                  <div className="font-mono" style={{ fontSize: 20, fontWeight: 700, color: seg.color }}>
                    {seg.size} <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 400 }}>{t.contacts}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Latifa's recommendation */}
      <div style={{
        background: '#FFF5EB', borderRadius: 12, padding: '16px 20px', marginTop: 24,
        border: '1px solid #FDDCB5', display: 'flex', alignItems: 'flex-start', gap: 12
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: '#B5651D22',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <IconSpeakerphone size={18} color="#B5651D" stroke={1.8} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#B5651D', marginBottom: 4 }}>{t.recommendation}</div>
          <div style={{ fontSize: 14, color: '#92400E', lineHeight: 1.6 }}>
            {lang === 'ar' ? industry.recommendationAr : industry.recommendationEn}
          </div>
        </div>
      </div>

      {/* Slide-over drawers */}
      {showTemplateDrawer && (
        <TemplateSlideOver lang={lang} t={t}
          onClose={() => setShowTemplateDrawer(false)}
          onSubmit={handleNewTemplate}
        />
      )}
      {showCampaignDrawer && (
        <CampaignSlideOver lang={lang} t={t}
          templates={localTemplates}
          segments={localSegments}
          contacts={contacts}
          tags={localTags}
          onAddTag={tag => setLocalTags(prev => [...prev, tag])}
          onAddSegment={seg => setLocalSegments(prev => [...prev, seg])}
          onClose={() => setShowCampaignDrawer(false)}
          onLaunch={handleLaunchCampaign}
        />
      )}
    </div>
  );
}
